import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import { encode, decode, getCurrentUser } from '../../../../../../helpers/utils';
import { setPinsByCategory, setEditingPinId, setCurrentFloor, setPinCount } from '../../../../../../store/slices/projectItemSlice';
import { deletePinApi, removePinApi, PlanExpiryDetails } from '../../../../Helpers/apis/otherApis';
import { fetchPinData } from '../../../../../../components/map/components/hooks/useLoadPins';
import { saveProduct, deleteSubPin, movePinToGroupApi } from '../services/productService';
import { useDeletePinPaths } from '../../../../../../components/map/components/Map/Navigation/useDeletePinPaths';
import { useProjectHeader } from '../../../../Helpers/pageDiv/ProjectHeaderContext';
import { PinCountApi } from '../../../../../../components/map/components/helpers/projectApi';

export const useProductList = ({ setModal, setPlanDetails }) => {
    const dispatch    = useDispatch();
    const navigate    = useNavigate();
    const { id }      = useParams();
    const decodedId   = decode(id);
    
    const pinCount    = useSelector((state) => state.api.pinCount);
    const projectData = useSelector((state) => state.api.projectData);
    const { handleEnableDisable } = useProjectHeader();
    const allPins     = useSelector((state) => state.api.allPins);
    const floorList   = useSelector((s) => s.api.floorList);
    
    const productList = allPins?.product ?? [];
    
    const { deletePinPaths } = useDeletePinPaths();
    
    const productListRef = useRef(productList);
    useEffect(() => { productListRef.current = productList; }, [productList]);
    
    const [searchTerm, setSearchTerm] = useState('');
    
    const filteredList = productList.filter(({ product_name = '', floor_plan = '', search_name = '', group_details = [] }) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const groupMatch = group_details.some((g) => g?.name?.toLowerCase().includes(term));
        return (
            product_name?.toLowerCase().includes(term) ||
            floor_plan?.toLowerCase().includes(term)   ||
            search_name?.toLowerCase().includes(term)  ||
            groupMatch
        );
    });
    
    const parentList = [
        { value: null, label: 'None' },
        ...productList
        .filter((item) => item.type === 2)
        .map((el) => ({ ...el, value: el.enc_id, label: el.product_name })),
    ];
    
    const syncProducts = useCallback(() => {
        fetchPinData(decodedId, ['product']).then((updated) => {
            dispatch(setPinsByCategory({ product: updated?.product }));
        });
    }, [decodedId, dispatch]);
    
    const refreshProducts = useCallback(async () => {
        const updated = await fetchPinData(decodedId, ['product']);
        dispatch(setPinsByCategory({ product: updated?.product }));
    }, [decodedId, dispatch]);
    
    const getCustomerId = () => {
        const user = getCurrentUser()?.user;
        return projectData?.enc_customer_id ?? user?.common_id;
    };
    
    const handleAddNew = useCallback( async () => {
        const isAtLimit = pinCount?.used_products >= pinCount?.total_products; 
        if (isAtLimit) {
            const plan = await PlanExpiryDetails(decodedId, setModal);
            setPlanDetails?.(plan);
            return;
        }
        document.getElementById('productSubmitBtn')?.click();
    }, [pinCount, decodedId, setPlanDetails, setModal]);
    
    const handleAddGroup = useCallback(async () => {

        const isAtLimit = pinCount?.used_products >= pinCount?.total_products; 
        if (isAtLimit) {
            const plan = await PlanExpiryDetails(decodedId, setModal);
            setPlanDetails?.(plan);
            return;
        }
        const customerId = getCustomerId();
        
        try { 
            const groupRes = await saveProduct({
                customer_id:   customerId,
                project_id:    decodedId,
                floor_plan_id: null,
                product_name:  '! New group',
                product_color: projectData?.product_color,
                type:          2,
            });
            
            if (groupRes.type !== 1) return;
            
            const newGroupId  = groupRes.response?.data?.id;
            const newGroupEnc = groupRes.response?.data?.enc_id ?? newGroupId;
            
            const subRes = newGroupId ? await saveProduct({
                customer_id:   customerId,
                project_id:    decodedId,
                floor_plan_id: null,
                product_name:  '! New product',
                product_color: projectData?.product_color,
                product_id:    newGroupId,
                type:          3,
            }) : null;
            
            const newSubId = subRes?.response?.data?.id;
            
            const newGroup = {
                enc_id:        newGroupEnc ?? newGroupId,
                product_name:  '! New group',
                product_color: projectData?.product_color,
                type:          2,
                positions:     null,
                floor_plan:    null,
                group_details: newSubId ? [{
                    id:            newSubId,
                    name:          '! New product',
                    product_color: projectData?.product_color,
                }] : [],
                category:      'product',
                title:         '! New group',
            };
            dispatch(setPinsByCategory({
                product: [...productListRef.current, newGroup],
            }));
            
            syncProducts();
        } catch (err) {
            console.error('Create pin group failed:', err);
        }
    }, [pinCount, projectData, decodedId, dispatch, syncProducts]);
    
    const handleAddGroupProduct = useCallback(async (group) => {
        const customerId = getCustomerId();
        
        try {
            const response = await saveProduct({
                customer_id:   customerId,
                project_id:    decodedId,
                floor_plan_id: null,
                product_name:  '! New product',
                product_color: projectData?.product_color,
                product_id:    group.enc_id,
                type:          3,
            });
            
            if (response.type !== 1) return;
            
            const newSubId = response.response?.data?.id;
            
            const updated = productListRef.current.map((p) => {
                if (p.enc_id !== group.enc_id) return p;
                return {
                    ...p,
                    group_details: [
                        ...(p.group_details ?? []),
                        {
                            id:            newSubId,
                            name:          '! New product',
                            product_color: p.product_color,
                        },
                    ],
                };
            });
            dispatch(setPinsByCategory({ product: updated }));
            
            syncProducts();
        } catch (err) {
            console.error('Create group product failed:', err);
        }
    }, [projectData, decodedId, dispatch, syncProducts]);
    
    const movePinToGroup = useCallback(async (pin, fromGroupId, toGroupId) => {
        if (!fromGroupId || !toGroupId || fromGroupId === toGroupId) return;
        
        const current = productListRef.current;
        const updated = current.map((p) => {
            if (p.enc_id === fromGroupId) {
                return { ...p, group_details: (p.group_details ?? []).filter((sp) => sp.id !== pin.id) };
            }
            if (p.enc_id === toGroupId) {
                return { ...p, group_details: [...(p.group_details ?? []), pin] };
            }
            return p;
        });
        dispatch(setPinsByCategory({ product: updated }));
        
        try {
            const customerId = getCustomerId();
            await movePinToGroupApi({
                product_id:   toGroupId,
                id:           pin.id,
                type:         3,
                product_name: pin.name,
                customer_id:  customerId,
                project_id:   decodedId,
            });
            handleEnableDisable();
            syncProducts();
        } catch (err) {
            console.error('Move pin to group failed:', err); 
            syncProducts();
        }
    }, [decodedId, dispatch, syncProducts]);
    
    const handleTotalPinCount = useCallback( async (id) => {  
        let pinCount = await PinCountApi(id) 
        dispatch(setPinCount(pinCount)); 
        // console.log(pinCount,"here lsd");
    }, []);
    
    const handleEdit = useCallback((product) => { 
        const found = floorList.find(
            (option) => String(option.enc_id) === String(product?.fp_id)
        );
        if (found?.enc_id) {
            dispatch(setCurrentFloor(found));
        }
        if(product?.product_id){
            navigate(encode(product.id), { state: { isSubPin: true } });
        }else{
            navigate(encode(product.enc_id));
        }
        dispatch(setEditingPinId(product.enc_id));
    }, [navigate, dispatch, floorList]);
    
    const handleRemove = useCallback((product, canDrag, isSubPin = false) => {
        if (isSubPin) {
            swal({
                title: 'Are you sure you want to delete?',
                icon:  'warning',
                buttons: {
                    cancel:  { text: 'Cancel', value: 'No',  visible: true, className: 'btn-danger', closeModal: true },
                    confirm: { text: 'Delete', value: 'Yes', visible: true, className: 'btn-red',    closeModal: true },
                },
            }).then((value) => {
                if (value !== 'Yes') return;
                
                const optimistic = productListRef.current.map((p) => ({
                    ...p,
                    group_details: p.group_details
                    ? p.group_details.filter((sp) => sp.id !== product.id)
                    : p.group_details,
                }));
                dispatch(setPinsByCategory({ product: optimistic }));
                
                deleteSubPin(product.id).then(() => handleEnableDisable()).catch(() => syncProducts());
            });
            return;
        }
        
        const buttons = {
            cancel:  { text: 'Cancel', value: 'No',     visible: true, className: 'btn-danger', closeModal: true },
            confirm: { text: 'Delete', value: 'Yes',    visible: true, className: 'btn-red',    closeModal: true },
            ...(!canDrag && {
                remove: { text: 'Remove From Floor Plan', value: 'Remove', visible: true, className: 'btn-danger', closeModal: true },
            }),
        };
        
        swal({
            title:   'Are you sure you want to delete?',
            icon:    'warning',
            buttons: !canDrag
            ? { cancel: buttons.cancel, remove: buttons.remove, confirm: buttons.confirm }
            : buttons,
        }).then(async (value) => {
            if (value === 'Yes') {
                const optimistic = productListRef.current.filter((p) => p.enc_id !== product.enc_id);
                dispatch(setPinsByCategory({ product: optimistic }));
                
                deletePinPaths(product.enc_id);
                
                deletePinApi(`product/${product.enc_id}`, projectData, decodedId, [], handleEnableDisable)
                .catch(() => syncProducts());
            }
            
            if (value === 'Remove') {
                const optimistic = productListRef.current.map((p) =>
                    p.enc_id === product.enc_id
                ? { ...p, positions: null, floor_plan: null, fp_id: null }
                : p
            );
            dispatch(setPinsByCategory({ product: optimistic }));
            
            deletePinPaths(product.enc_id);
            
            let removePin = await removePinApi('remove-pin', { type: 2, id: product.enc_id }, projectData, decodedId, [], handleEnableDisable)
            .catch(() => syncProducts());
            handleTotalPinCount(decodedId)
        }
    });
}, [dispatch, projectData, decodedId, syncProducts, deletePinPaths]);

const getList = async () =>{
        try {
            const updated = await fetchPinData(decodedId, ['product']);
            dispatch(setPinsByCategory({ product: updated?.product }));
        } catch (error) {
            console.log(error);
        }
    }

const handleCreateProduct = async (values, { setFieldError }) => {
    if (values?.enc_id && values?.isDrop) {
        handleAddNew();
        return;
    }
    
    const customerId = getCustomerId();
    
    const payload = {
        customer_id:    customerId,
        project_id:     decodedId,
        floor_plan_id:  null,
        product_name:   '! New product',
        tags:           values.tags ?? [],
        product_code:   '',
        description:    '',
        positions:      null,
        website:        JSON.stringify([]),
        specifications: JSON.stringify([]),
        product_color:  projectData?.product_color,
        boundary_color: null,
    };
    
    try {
        const response = await saveProduct(payload);
        if (response.type === 1) {
            const newId = response.response?.data?.id;
            
            const newPin = {
                enc_id:        newId,
                product_name:  '! New product',
                product_color: projectData?.product_color,
                type:          1,
                positions:     null,
                floor_plan:    null,
                group_details: [],
                category:      'product',
                title:         '! New product',
            };
            dispatch(setPinsByCategory({
                product: [...productListRef.current, newPin],
            }));
            
            handleEnableDisable();
            syncProducts();
        }
    } catch (err) {
        console.error('Create product failed:', err);
    }
};

return {
    filteredList,
    parentList,
    searchTerm,
    setSearchTerm,
    handleAddNew,
    handleAddGroup,
    handleAddGroupProduct,
    movePinToGroup,
    handleEdit,
    handleRemove,
    handleCreateProduct,
    refreshProducts,
    getList
};
};
